import { FC, useState } from "react";
import { useQuery } from "convex/react";
import { api, Id } from "@/smart-views/client/api";
import {
  MDSActionMenu,
  MDSActionMenuItem,
  MDSIconButton,
  MDSIconButtonSize,
  MDSIconButtonVariant,
  MDSIconKind,
  MDSMenuDropdown,
  MDSMenuDropdownSlotName,
  MDSMenuItemHeader,
  MDSMenuItemHeaderVariant,
  MDSMenuItemVariant,
} from "@/mds";

interface BlockMenuProps {
  blockId: Id<"blocks">;
  updatedAt: number;
  isCollapsed: boolean;
  onToggleCollapsed: () => void;
  onEditPrompt: () => void;
  onDelete: () => void;
  onRestoreVersion: (versionId: Id<"blockVersions">) => void;
  onOpenChange?: (isOpen: boolean) => void;
}

export const BlockMenu: FC<BlockMenuProps> = ({
  blockId,
  updatedAt,
  isCollapsed,
  onToggleCollapsed,
  onEditPrompt,
  onDelete,
  onRestoreVersion,
  onOpenChange,
}) => {
  const versions = useQuery(api.smartViews.client.listBlockVersions, { blockId });
  const [isOpen, setIsOpen] = useState(false);
  // Skip the most recent version — it's the current one.
  const previousVersions = versions?.slice(1) ?? [];
  const setMenuOpen = (nextIsOpen: boolean) => {
    setIsOpen(nextIsOpen);
    onOpenChange?.(nextIsOpen);
  };

  return (
    <MDSMenuDropdown isOpen={isOpen} onOpenChange={setMenuOpen}>
      <MDSIconButton
        slot={MDSMenuDropdownSlotName.Trigger}
        size={MDSIconButtonSize.XSmall}
        iconKind={MDSIconKind.More}
        variant={MDSIconButtonVariant.OnChrome}
        aria-label="Block actions"
        rounded
      />
      <MDSActionMenu
        aria-label="Block actions"
        style={{ minWidth: 260 }}
        slot={MDSMenuDropdownSlotName.Menu}
      >
        <MDSActionMenuItem
          id="edit-prompt"
          variant={MDSMenuItemVariant.OneLine}
          label="Edit prompt"
          icon={MDSIconKind.Compose}
          onClick={onEditPrompt}
        />
        <MDSActionMenuItem
          id={isCollapsed ? "expand-block" : "collapse-block"}
          variant={MDSMenuItemVariant.OneLine}
          label={isCollapsed ? "Expand block" : "Collapse block"}
          icon={isCollapsed ? MDSIconKind.Expand : MDSIconKind.Collapse}
          onClick={onToggleCollapsed}
        />
        <MDSActionMenuItem
          id="delete"
          variant={MDSMenuItemVariant.OneLine}
          label="Delete block"
          icon={MDSIconKind.Trash}
          onClick={onDelete}
        />
        {previousVersions.length > 0 && (
          <MDSMenuItemHeader
            variant={MDSMenuItemHeaderVariant.Middle}
            label="Restore earlier version"
          />
        )}
        {previousVersions.map(version => (
          <MDSActionMenuItem
            key={version._id}
            id={version._id}
            variant={MDSMenuItemVariant.OneLine}
            label={`${labelForVersion(version.versionNumber)} · ${labelForReason(version.reason)}`}
            extraText={formatRelative(version.createdAt)}
            icon={MDSIconKind.History}
            onClick={() => onRestoreVersion(version._id)}
          />
        ))}
        <MDSMenuItemHeader
          variant={MDSMenuItemHeaderVariant.Middle}
          label={`Last updated ${formatRelative(updatedAt)}`}
        />
      </MDSActionMenu>
    </MDSMenuDropdown>
  );
};

const labelForReason = (reason: string | undefined): string => {
  if (!reason) return "change";
  if (reason === "create") return "created";
  if (reason === "title-edit") return "title edited";
  if (reason === "content-edit") return "content edited";
  if (reason.startsWith("restore-v")) return `restored from ${reason.replace("restore-", "")}`;
  return reason;
};

const labelForVersion = (versionNumber: number | undefined): string => {
  return versionNumber === undefined ? "Earlier version" : `v${versionNumber}`;
};

const formatRelative = (timestamp: number): string => {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);
  if (seconds < 60) return "just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
};
